'use client';

import * as Icons from 'lucide-react';
import { ArrowRight } from 'lucide-react';

function LucideIcon({ name, size = 28, color }) {
  const Icon = Icons[name] || Icons.Sparkles;
  return <Icon size={size} color={color} strokeWidth={2} />;
}

export default function Features({ features, onlyHome = true }) {
  if (!features || features.length === 0) return null;

  // Filtre par show_on_home si demandé (cas de la home page).
  // Pour les widgets dans pages CMS, le caller passe onlyHome=false ou
  // une liste déjà filtrée.
  const visible = onlyHome
    ? features.filter(f => f.show_on_home !== false)
    : features;
  if (visible.length === 0) return null;

  return (
    <section id="features" style={{ padding: '100px 0', background: '#fff' }}>
      <div className="container-page">
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <span style={{
            display: 'inline-block',
            padding: '6px 14px',
            borderRadius: '999px',
            background: 'rgba(226, 114, 40, 0.1)',
            color: 'var(--color-primary)',
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '16px',
          }}>
            FONCTIONNALITES
          </span>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            Tout ce qu'il vous faut, dans un seul outil
          </h2>
          <p className="section-subtitle">
            Conçu spécifiquement pour les métiers du bâtiment. Pas de fonctionnalités superflues, juste l'essentiel pour faire tourner votre activité.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
        }}>
          {visible.map((feature, idx) => (
            <FeatureCard key={feature.id || idx} feature={feature} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeatureCard({ feature, index = 0 }) {
  // Si url définie : rendre dans un <a> clickable, sinon en simple div.
  const hasLink = !!feature.url;
  const Tag = hasLink ? 'a' : 'div';
  const tagProps = hasLink ? {
    href: feature.url,
    target: feature.target || '_self',
    rel: feature.target === '_blank' ? 'noopener noreferrer' : undefined,
  } : {};

  return (
    <Tag
      {...tagProps}
      style={{
        padding: '32px',
        borderRadius: '16px',
        background: '#fff',
        border: '1px solid var(--color-border)',
        transition: 'all 0.25s',
        cursor: hasLink ? 'pointer' : 'default',
        animation: `slideUp 0.6s ease-out ${index * 0.08}s backwards`,
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        position: 'relative',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.08)';
        e.currentTarget.style.borderColor = 'var(--color-primary)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'var(--color-border)';
      }}
    >
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '12px',
        background: 'rgba(226, 114, 40, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '20px',
      }}>
        <LucideIcon name={feature.icon} color="var(--color-primary)" />
      </div>
      <h3 style={{
        fontSize: '19px',
        fontWeight: 700,
        margin: '0 0 12px',
        letterSpacing: '-0.01em',
      }}>
        {feature.title}
      </h3>
      <p style={{
        fontSize: '15px',
        lineHeight: 1.6,
        color: 'var(--color-text-muted)',
        margin: 0,
      }}>
        {feature.description}
      </p>
      {hasLink && (
        <div style={{
          marginTop: 18,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          color: 'var(--color-primary)',
          fontSize: 13, fontWeight: 600,
        }}>
          En savoir plus <ArrowRight size={14} />
        </div>
      )}
    </Tag>
  );
}
